import { DomainError } from './errors.ts';
import { err, ok, type Result } from './result.ts';

const keysOf = <O extends object>(o: O): (keyof O & string)[] => Object.keys(o) as (keyof O & string)[];

type TransitionTable = Readonly<Record<string, Readonly<Record<string, string>>>>;

type StateOf<T extends TransitionTable> = keyof T & string;
type EventOf<T extends TransitionTable> = { [S in keyof T]: keyof T[S] & string }[keyof T];

export interface Machine<T extends TransitionTable> {
  /** Переход с проверкой на уровне типов: событие должно быть допустимо для состояния. */
  transition<S extends StateOf<T>, E extends keyof T[S] & string>(state: S, event: E): T[S][E];
  /** Переход для значений, известных только в рантайме. */
  tryTransition(state: string, event: string): Result<StateOf<T>, DomainError>;
  can(state: string, event: string): boolean;
  events<S extends StateOf<T>>(state: S): (keyof T[S] & string)[];
  readonly states: readonly StateOf<T>[];
}

/**
 * Типизированная машина состояний: таблица «состояние → событие → новое состояние».
 * Недопустимый переход не проходит проверку типов, а в рантайме даёт `common.invalid_transition`.
 */
export function createMachine<const T extends TransitionTable>(
  table: T & { [S in keyof T]: { [E in keyof T[S]]: StateOf<T> } },
): Machine<T> {
  const lookup = (state: string, event: string): string | undefined =>
    Object.hasOwn(table, state) && Object.hasOwn(table[state] ?? {}, event)
      ? table[state]?.[event]
      : undefined;

  const tryTransition = (state: string, event: string): Result<StateOf<T>, DomainError> => {
    const next = lookup(state, event);
    return next === undefined
      ? err(new DomainError('common.invalid_transition', { state, event }))
      : ok(next as StateOf<T>);
  };

  return {
    transition<S extends StateOf<T>, E extends keyof T[S] & string>(state: S, event: E): T[S][E] {
      const r = tryTransition(state, event);
      if (!r.ok) throw r.error;
      return r.value as T[S][E];
    },
    tryTransition,
    can: (state, event) => lookup(state, event) !== undefined,
    events(state) {
      return keysOf(table[state]);
    },
    states: keysOf(table),
  };
}

export type { EventOf, StateOf };
